import { ensureGameSession, type GameSessionHandle } from '@sorrem-client/game/game-session-store';
import { getStoredCrucibleJoinSession } from '@sorrem-client/ui/features/PlayPage/stored-join-session';
import { getStoredSessionToken } from '@sorrem-client/ui/shared/auth-session';
import type { CrucibleRecord, CrucibleSessionConnection } from '@sorrem/shared';
import { useEffect, useRef, useState } from 'react';

interface RunHandoffInput {
    readonly instanceId: string | null;
    /** The host's connection, once it is up for a run that started while the page was open. */
    readonly readyPayload: CrucibleSessionConnection | null;
    readonly crucible: CrucibleRecord | null;
    /** The page's game session, drawing the gathering. */
    readonly session: GameSessionHandle | undefined;
}

/**
 * Whether the run has taken over the page's scene. It starts on the scene
 * that has been drawing the gathering, so nothing it loaded is loaded again.
 */
export function useRunHandoff({ instanceId, readyPayload, crucible, session }: RunHandoffInput): boolean {
    const [isRunStarted, setIsRunStarted] = useState(false);

    // The host is up: the run starts here.
    useEffect(() => {
        if (readyPayload === null || !instanceId || session === undefined) {
            return;
        }
        ensureGameSession(instanceId, readyPayload, getStoredSessionToken() ?? '');
        setIsRunStarted(true);
    }, [instanceId, readyPayload, session]);

    /*
     * Arriving at a run already under way (a reload mid-run): rejoin it with
     * the connection stored when the host became ready. Decided on the first
     * party state only, so a run that starts while the page is open waits
     * for its own `party:ready` instead.
     */
    const firstStartedAtRef = useRef<string | null | undefined>(undefined);
    useEffect(() => {
        if (crucible === null || session === undefined || firstStartedAtRef.current !== undefined) {
            return;
        }
        firstStartedAtRef.current = crucible.startedAt;
        const stored = getStoredCrucibleJoinSession();
        if (crucible.startedAt === null || stored === null || stored.crucibleId !== crucible.id) {
            return;
        }
        ensureGameSession(crucible.id, stored.sessionConnection, getStoredSessionToken() ?? '');
        setIsRunStarted(true);
    }, [crucible, session]);

    return isRunStarted;
}
